import { useMemo } from 'react';
import type { CategoryDto } from '@trestly/shared';
import { useCategories } from '../api/hooks';
import { t } from '@/i18n';

/**
 * Фирменная палитра конвертов. Цвет, выбранный человеком, хранится в
 * categories.color; пока он не выбран, конверт красится по позиции — так у
 * соседних конвертов цвета не совпадают.
 */
export const ENVELOPE_PALETTE = [
  '#2f9e6e',
  '#e08a1e',
  '#7a5af8',
  '#2e7cf6',
  '#d9467a',
  '#139fb5',
  '#9a6b3c',
  '#5b6b7a',
] as const;

/** «Без категории» — нейтральный тёплый серый. */
export const NO_CATEGORY_COLOR = '#8a8076';

export function paletteAt(position: number): string {
  const n = ENVELOPE_PALETTE.length;
  return ENVELOPE_PALETTE[((position % n) + n) % n] ?? NO_CATEGORY_COLOR;
}

export function envelopeColor(color: string | null | undefined, position: number): string {
  return color ?? paletteAt(position);
}

export interface EnvelopeLook {
  name: string;
  color: string;
}

/** Геттер, а не константа: имя зависит от текущего языка. */
export function noCategoryLook(): EnvelopeLook {
  return { name: t('lib.noCategory'), color: NO_CATEGORY_COLOR };
}

/** Совместимость: name читается геттером, поэтому язык всегда текущий. */
export const NO_CATEGORY: EnvelopeLook = {
  get name() {
    return t('lib.noCategory');
  },
  color: NO_CATEGORY_COLOR,
};

/** id → имя и цвет конверта. null — «Без категории». Архивные тоже: на них ссылается история. */
export function useEnvelopeLook(): (id: number | null) => EnvelopeLook {
  const categories = useCategories(true);
  return useMemo(() => {
    const map = new Map<number, EnvelopeLook>();
    for (const c of categories.data ?? []) map.set(c.id, lookOf(c));
    return (id: number | null) =>
      id === null ? noCategoryLook() : (map.get(id) ?? { name: t('lib.category'), color: NO_CATEGORY_COLOR });
  }, [categories.data]);
}

export function lookOf(category: Pick<CategoryDto, 'name' | 'color' | 'position'>): EnvelopeLook {
  return { name: category.name, color: envelopeColor(category.color, category.position) };
}
